import { ref } from "vue";
import { toast } from "@/ui-plus/sonner";

const APP_NAME = "Autoprint";
const MAC_LAUNCH_AGENT_LABEL = "com.zemega.autoprint";

export const isNwApp = typeof global !== "undefined";
export const autoStartOnLogin = ref(false);
export const autoStartLoading = ref(false);

export const autoStartPlatformLabel = (() => {
    if (!isNwApp) return "";
    if (process.platform === "win32") return "Windows";
    if (process.platform === "darwin") return "macOS";
    if (process.platform === "linux") return "Linux";
    return process.platform;
})();

export const isAutoStartSupported = isNwApp && ["win32", "darwin", "linux"].includes(process.platform);

function getLaunchPath() {
    const execPath = process.execPath;
    if (process.platform === "darwin") {
        const match = execPath.match(/^(.+\.app)/);
        if (match) {
            return match[1];
        }
    }
    return execPath;
}

function normalizePathForCompare(filePath: string) {
    const path = require("path");
    if (process.platform === "win32") {
        return path.win32.normalize(filePath).toLowerCase();
    }
    return path.normalize(filePath);
}

function getWindowsRegExe() {
    const path = require("path");
    const systemRoot = process.env.SystemRoot || process.env.windir || "C:\\Windows";
    return path.win32.join(systemRoot, "System32", "reg.exe");
}

function runWindowsReg(args: string) {
    const { execSync } = require("child_process");
    const regExe = getWindowsRegExe();
    return execSync(`"${regExe}" ${args}`, { windowsHide: true, encoding: "utf8" });
}

function getWindowsAutoStartPath() {
    try {
        const out = runWindowsReg(`query "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run" /v "${APP_NAME}"`) as string;
        for (const line of out.split(/\r?\n/)) {
            if (line.includes(APP_NAME) && line.includes("REG_SZ")) {
                const regSzIndex = line.indexOf("REG_SZ");
                return line.slice(regSzIndex + "REG_SZ".length).trim();
            }
        }
        return null;
    } catch {
        return null;
    }
}

function isWindowsAutoStartPathCurrent() {
    const storedPath = getWindowsAutoStartPath();
    if (!storedPath) {
        return false;
    }
    return normalizePathForCompare(storedPath) === normalizePathForCompare(getLaunchPath());
}

function isWindowsAutoStartEnabled() {
    return getWindowsAutoStartPath() !== null;
}

function setWindowsAutoStart(enabled: boolean) {
    const launchPath = getLaunchPath();

    if (enabled) {
        if (isWindowsAutoStartPathCurrent()) {
            return;
        }
        runWindowsReg(
            `add "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run" /v "${APP_NAME}" /t REG_SZ /d "${launchPath}" /f`
        );
        return;
    }

    try {
        runWindowsReg(`delete "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run" /v "${APP_NAME}" /f`);
    } catch {
        // already removed
    }
}

function getMacLaunchAgentPath() {
    const path = require("path");
    const os = require("os");
    return path.join(os.homedir(), "Library", "LaunchAgents", `${MAC_LAUNCH_AGENT_LABEL}.plist`);
}

function escapeXml(value: string) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function buildMacLaunchAgentPlist(launchPath: string) {
    const isAppBundle = launchPath.endsWith(".app");
    const programArgs = isAppBundle
        ? [
            "<string>/usr/bin/open</string>",
            "<string>-a</string>",
            `<string>${escapeXml(launchPath)}</string>`,
        ]
        : [`<string>${escapeXml(launchPath)}</string>`];

    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>Label</key>
    <string>${MAC_LAUNCH_AGENT_LABEL}</string>
    <key>ProgramArguments</key>
    <array>
        ${programArgs.join("\n        ")}
    </array>
    <key>RunAtLoad</key>
    <true/>
    <key>ProcessType</key>
    <string>Interactive</string>
</dict>
</plist>
`;
}

function isMacLaunchAgentLoaded() {
    const { execSync } = require("child_process");
    const uid = require("os").userInfo().uid;
    try {
        execSync(`launchctl print "gui/${uid}/${MAC_LAUNCH_AGENT_LABEL}"`, { stdio: "ignore" });
        return true;
    } catch {
        return false;
    }
}

function unloadMacLaunchAgent() {
    if (!isMacLaunchAgentLoaded()) {
        return;
    }

    const { execSync } = require("child_process");
    const uid = require("os").userInfo().uid;
    const stdio = { stdio: "ignore" as const };

    try {
        execSync(`launchctl bootout "gui/${uid}/${MAC_LAUNCH_AGENT_LABEL}"`, stdio);
    } catch {
        // ignore
    }
}

function loadMacLaunchAgent(plistPath: string) {
    if (isMacLaunchAgentLoaded()) {
        return;
    }

    const { execSync } = require("child_process");
    const uid = require("os").userInfo().uid;
    const stdio = { stdio: "ignore" as const };

    try {
        execSync(`launchctl bootstrap "gui/${uid}" "${plistPath}"`, stdio);
        return;
    } catch {
        // older macOS
    }

    execSync(`launchctl load -w "${plistPath}"`, stdio);
}

function isMacAutoStartEnabled() {
    const fs = require("fs");
    return fs.existsSync(getMacLaunchAgentPath());
}

function setMacAutoStart(enabled: boolean) {
    const fs = require("fs");
    const path = require("path");
    const plistPath = getMacLaunchAgentPath();

    unloadMacLaunchAgent();

    if (fs.existsSync(plistPath)) {
        fs.unlinkSync(plistPath);
    }

    if (!enabled) {
        return;
    }

    const launchPath = getLaunchPath();
    fs.mkdirSync(path.dirname(plistPath), { recursive: true });
    fs.writeFileSync(plistPath, buildMacLaunchAgentPlist(launchPath), "utf8");
    loadMacLaunchAgent(plistPath);
}

function getLinuxDesktopFilePath() {
    const path = require("path");
    const os = require("os");
    return path.join(os.homedir(), ".config", "autostart", "autoprint.desktop");
}

function isLinuxAutoStartEnabled() {
    const fs = require("fs");
    return fs.existsSync(getLinuxDesktopFilePath());
}

function setLinuxAutoStart(enabled: boolean) {
    const fs = require("fs");
    const path = require("path");
    const desktopFilePath = getLinuxDesktopFilePath();

    if (!enabled) {
        if (fs.existsSync(desktopFilePath)) {
            fs.unlinkSync(desktopFilePath);
        }
        return;
    }

    const launchPath = getLaunchPath();
    const desktopFile = [
        "[Desktop Entry]",
        "Type=Application",
        `Name=${APP_NAME}`,
        `Exec=${launchPath}`,
        "Hidden=false",
        "NoDisplay=false",
        "X-GNOME-Autostart-enabled=true",
        "",
    ].join("\n");

    fs.mkdirSync(path.dirname(desktopFilePath), { recursive: true });
    fs.writeFileSync(desktopFilePath, desktopFile, "utf8");
}

function needsAutoStartPathRefresh() {
    if (!isAutoStartEnabledInOs()) {
        return false;
    }

    const currentLaunchPath = getLaunchPath();

    if (process.platform === "win32") {
        return !isWindowsAutoStartPathCurrent();
    }

    if (process.platform === "darwin") {
        const fs = require("fs");
        const content = fs.readFileSync(getMacLaunchAgentPath(), "utf8");
        return !content.includes(escapeXml(currentLaunchPath));
    }

    const fs = require("fs");
    const content = fs.readFileSync(getLinuxDesktopFilePath(), "utf8");
    return !content.includes(`Exec=${currentLaunchPath}`);
}

export function isAutoStartEnabledInOs() {
    if (!isAutoStartSupported) {
        return false;
    }

    if (process.platform === "win32") {
        return isWindowsAutoStartEnabled();
    }
    if (process.platform === "darwin") {
        return isMacAutoStartEnabled();
    }
    return isLinuxAutoStartEnabled();
}

export async function applyAutoStart(enabled: boolean) {
    if (!isAutoStartSupported) {
        throw new Error(`Auto start is not supported on ${process.platform}`);
    }

    if (process.platform === "win32") {
        setWindowsAutoStart(enabled);
        return;
    }
    if (process.platform === "darwin") {
        setMacAutoStart(enabled);
        return;
    }
    setLinuxAutoStart(enabled);
}

export async function refreshAutoStartStatus() {
    if (!isAutoStartSupported) {
        return;
    }

    autoStartOnLogin.value = isAutoStartEnabledInOs();
}

export async function syncAutoStartOnBoot() {
    if (!isAutoStartSupported) {
        return;
    }

    // Refresh launch path only when OS entry exists but path is stale (e.g. app was moved).
    if (needsAutoStartPathRefresh()) {
        await applyAutoStart(true);
    }

    await refreshAutoStartStatus();
}

export async function toggleAutoStart(enabled: boolean) {
    if (!isAutoStartSupported || autoStartLoading.value) {
        return;
    }

    autoStartLoading.value = true;

    try {
        await applyAutoStart(enabled);
        await refreshAutoStartStatus();
        toast.success(enabled ? "Auto start enabled" : "Auto start disabled");
    } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to update auto start");
        await refreshAutoStartStatus();
    } finally {
        autoStartLoading.value = false;
    }
}
